import * as React from 'react';
import { DEFAULT_DIGIT_INPUT_COUNT } from '../../utils/constants';

const DIGIT_INPUT_SELECTOR = 'digitInput';
type CodeDigit = number | '';
export type NullableCodeDigit = number | null;
interface VerificationCodeComponentProperties {
    setClearCode?: (clearCode: () => void) => void;
    onCodeEntered: (code: number[]) => void;
    onCodeDigitChanged?: () => void;
    initialCode?: number[];
    onCodeChanged?: (code: NullableCodeDigit[]) => void;
}

interface VerificationCodeComponentState {
    code: CodeDigit[];
    selectedInputIndex: number;
}

class VerificationCodeComponent extends React.Component<VerificationCodeComponentProperties, VerificationCodeComponentState> {
    private focusIsOn: boolean = false;

    constructor(props: any) {
        super(props);
        if (props.setClearCode) {
            props.setClearCode(this.clearCode.bind(this));
        }
        this.state = {
            code: this.props.initialCode ?
                this.props.initialCode :
                new Array(DEFAULT_DIGIT_INPUT_COUNT),
            selectedInputIndex: 0
        };

        this.clearCode = this.clearCode.bind(this);
    }

    render() {
        if (this.focusIsOn) {
            this.focusDigitInput();
        }
        let inputList = [];
        for (let i = 0; i < DEFAULT_DIGIT_INPUT_COUNT; i++) {
            let inputValue = typeof this.state.code[i] === 'number' ? this.state.code[i].toString() : '';
            const input = (
                <input
                    autoFocus={i === 0}
                    id={`${DIGIT_INPUT_SELECTOR}${i}`}
                    key={i}
                    type="text"
                    className={'form-control col ' + DIGIT_INPUT_SELECTOR}
                    onChange={(event) => this.handleCodeChange(event)}
                    onClick={() => this.switchToDigitInput(i)}
                    onFocus={() => { this.focusIsOn = true; }}
                    onBlur={() => { this.focusIsOn = false; }}
                    onKeyDown={() => this.switchToDigitInput(i)}
                    value={inputValue}
                />
            );
            inputList.push(input);
        }

        return (inputList);
    }
    componentDidMount() {
        document.addEventListener('keydown', this.clearField);
        document.addEventListener('keydown', this.handleCodeRemove);
        document.addEventListener('keydown', this.handleArrowMovement);
    }

    componentWillUnmount() {
        document.removeEventListener('keydown', this.handleCodeRemove);
        document.removeEventListener('keydown', this.handleArrowMovement);
        document.removeEventListener('keydown', this.clearField);
    }

    allDigitsEntered = (): boolean => {
        for (let i = 0; i < DEFAULT_DIGIT_INPUT_COUNT; i++) {
            if (typeof this.state.code[i] !== 'number') {
                return false;
            }
        }
        return true;
    }

    private clearCode() {
        const codeDigits: CodeDigit[] = [];
        this.setState({code: codeDigits, selectedInputIndex: 0});
        this.focusDigitInput();
    }

    private clearField = (event: any): void => {
        if (!this.focusIsOn) { return; }
    }

    private handlePaste = (digits: string): boolean => {
        const code = digits.replace(/\D/g, '');
        const codeNum: number | null = parseInt(code, 10) || null;

        if (code.length !== DEFAULT_DIGIT_INPUT_COUNT || !codeNum) {
            return false;
        }

        const codeDigits: CodeDigit[] = [];
        for (let i = 0; i < code.length; i++) {
            codeDigits.push(parseInt(code[i], 10));
        }

        this.setState(
            { code: codeDigits },
            () => {
                this.switchToDigitInput(DEFAULT_DIGIT_INPUT_COUNT - 1);
                this.props.onCodeEntered(this.getCodeNumbers());
            }
        );
        return true;
    }

    private getCodeNumbers(): number[] {
        const code: number[] = [];
        for (let i = 0; i < this.state.code.length; i++) {
            if (typeof this.state.code[i] === 'number') {
                code.push(this.state.code[i] as number);
            }
        }
        return code;
    }

    private switchToDigitInput = (index: number) => {
        if (this.state.code.filter(x => x !== '').length === 0) {
            const firstDigitInput = document.getElementById(`${DIGIT_INPUT_SELECTOR}0`);
            if (firstDigitInput) {
                firstDigitInput.focus();
                index = 0;
            }
        }

        this.setState({
            selectedInputIndex: index
        });
    }

    private handleArrowMovement = (event: any) => {
        if (!this.focusIsOn) { return; }

        switch (event.key) {
            case 'ArrowLeft':
                const reducedIndex = this.state.selectedInputIndex - 1;
                this.switchToDigitInput(Math.max(reducedIndex, 0));
                break;
            case 'ArrowRight':
                const increasedIndex = this.state.selectedInputIndex + 1;
                this.switchToDigitInput(Math.min(increasedIndex, DEFAULT_DIGIT_INPUT_COUNT - 1));
                break;
            default:
                break;
        }
    }

    private handleCodeRemove = (event: any) => {
        if (!this.focusIsOn) { return; }

        if (event.key === 'Backspace' || event.key === 'Delete') {
            this.notifyCodeChange();
            this.saveAndMoveToPreviousDigitInput();
        }
    }

    private handleCodeChange = (event: any) => {
        const value: string = event.target.value;

        let digit: number = parseInt(value, 10);
        if (isNaN(digit)) { return; }

        if (this.props.onCodeDigitChanged) {
            this.props.onCodeDigitChanged();
        }

        if (digit > 9) {
            digit = digit % 10;
        }

        const codePasted: boolean = this.handlePaste(value);
        if (codePasted) { return; }
        this.notifyCodeChange();
        this.saveAndMoveToNextDigitInput(digit);

    }

    private notifyCodeChange = () => {
        if (!this.props.onCodeChanged) {
            return;
        }
        const digits = [];
        for (let i = 0; i < DEFAULT_DIGIT_INPUT_COUNT ; i++) {
            let digitInput = (document.getElementById(`${DIGIT_INPUT_SELECTOR}${i}`) as any).value;
            digits.push(digitInput === '' ? null : parseInt(digitInput, 10));
        }
        this.props.onCodeChanged(digits);
    }

    private saveAndMoveToNextDigitInput = (digit: number) => {
        const code = this.state.code;
        code[this.state.selectedInputIndex] = digit;

        this.setState(
            {
                selectedInputIndex: Math.min(this.state.selectedInputIndex + 1, DEFAULT_DIGIT_INPUT_COUNT - 1),
                code
            },
            this.handleAllDigitsEntered
        );
    }

    private saveAndMoveToPreviousDigitInput = () => {
        const code = this.state.code;
        code[this.state.selectedInputIndex] = '';

        this.setState({
            selectedInputIndex: Math.max(this.state.selectedInputIndex - 1, 0),
            code
        });
    }

    private handleAllDigitsEntered = () => {
        if (this.allDigitsEntered()) {
           this.props.onCodeEntered(this.getCodeNumbers());
        }
    }

    private focusDigitInput = (): void => {
        const getElements = document.getElementsByClassName(DIGIT_INPUT_SELECTOR);
        const digitInputs: HTMLInputElement[] =
            Array.from(getElements) as HTMLInputElement[];
        for (let i = 0; i < digitInputs.length; i++) {
            let digitInput = digitInputs[i];
            if (i === this.state.selectedInputIndex) {
                const endOfInputSelection = digitInput.value.length;
                digitInput.setSelectionRange(endOfInputSelection, endOfInputSelection);
                digitInput.focus();
                return;
            }
        }
    }
}

export default VerificationCodeComponent;



// WEBPACK FOOTER //
// ./src/webportal/components/authentication/VerificationCodeComponent.tsx